import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppHeader, SourceNote } from "@/components/Layout";
import { api, errMsg } from "@/lib/api";
import { useSeo, SITE_URL } from "@/lib/seo";
import { renderMarkdown, formatDate, imageUrl, articleJsonLd, breadcrumbJsonLd } from "@/lib/blog";
import { Clock, Twitter, Facebook, Linkedin, MessageCircle, Link as LinkIcon, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ShareBar = ({ post }) => {
  // Humans see this clean URL in the browser and when they copy the link.
  const articleUrl = `${SITE_URL}/blog/${post.slug}`;
  // Social scrapers (FB, X, LinkedIn, WhatsApp, iMessage) fetch the URL and read
  // the HTML before any JS runs, so we route them through the backend's share
  // endpoint — it returns proper OG tags server-side and redirects humans back
  // to the clean article URL. Previews now always show the blog hero + title,
  // never the SPA's homepage default meta.
  const shareUrl = `${SITE_URL}/api/blog/share/${post.slug}`;
  const text = encodeURIComponent(post.title);
  const u = encodeURIComponent(shareUrl);
  const items = [
    { href: `https://twitter.com/intent/tweet?url=${u}&text=${text}`, icon: Twitter, label: "X", testid: "share-x" },
    { href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, icon: Facebook, label: "Facebook", testid: "share-facebook" },
    { href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, icon: Linkedin, label: "LinkedIn", testid: "share-linkedin" },
    { href: `https://wa.me/?text=${text}%20${u}`, icon: MessageCircle, label: "WhatsApp", testid: "share-whatsapp" },
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(articleUrl); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-bar">
      {items.map(({ href, icon: Icon, label, testid }) => (
        <a key={label} href={href} target="_blank" rel="noreferrer noopener" aria-label={`Share on ${label}`}
           data-testid={testid}
           className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition-colors hover:bg-slate-900 hover:text-white hover:border-slate-900">
          <Icon className="h-4 w-4" />
        </a>
      ))}
      <button onClick={copy} aria-label="Copy link" data-testid="share-copy"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition-colors hover:bg-slate-900 hover:text-white hover:border-slate-900">
        <LinkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

const FAQBlock = ({ faq }) => {
  const [open, setOpen] = useState(0);
  if (!faq?.length) return null;
  return (
    <section className="mt-12 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="post-faq">
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Common questions</h2>
      <ul className="mt-5 divide-y divide-slate-200">
        {faq.map((f, i) => (
          <li key={i} className="py-3">
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-item-${i}`}
              className="flex w-full items-start justify-between gap-4 text-left text-base font-semibold text-slate-900"
            >
              <span>{f.q}</span>
              <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-slate-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="mt-2 whitespace-pre-line text-slate-600">{f.a}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/posts/${slug}`)
      .then((p) => { setPost(p); return api.get(`/blog/posts/${slug}/related`); })
      .then(setRelated)
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); else toast.error(errMsg(e)); });
  }, [slug]);

  const jsonLd = useMemo(() => {
    if (!post) return null;
    const list = [articleJsonLd(post, SITE_URL),
                  breadcrumbJsonLd([
                    { name: "Blog", path: "/blog" },
                    ...(post.category ? [{ name: post.category.name, path: `/blog/category/${post.category.slug}` }] : []),
                    { name: post.title, path: `/blog/${post.slug}` },
                  ], SITE_URL)];
    if (post.faq?.length) {
      list.push({
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    return list;
  }, [post]);

  useSeo({
    title: post?.meta_title || post?.title || "Blog",
    description: post?.meta_description || post?.excerpt || "Food Recall Alerts blog",
    path: `/blog/${slug}`,
    image: post?.hero_image ? imageUrl(post.hero_image).replace(SITE_URL, "") : undefined,
    jsonLd,
    keywords: post?.keywords,
  });

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="mx-auto max-w-xl px-5 py-16 text-center" data-testid="post-not-found">
          <h1 className="font-display text-3xl font-bold text-slate-900">Post not found</h1>
          <p className="mt-3 text-slate-600">The article you were looking for isn't here.</p>
          <button onClick={() => nav("/blog")} className="mt-6 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white">
            Back to the blog
          </button>
        </div>
      </div>
    );
  }

  if (!post) return <div className="p-10 text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <article className="mx-auto max-w-3xl px-5 py-10 sm:py-14" data-testid={`post-${post.slug}`}>
        <nav className="mb-6 text-xs text-slate-500">
          <Link to="/blog" className="hover:text-slate-900">Blog</Link>
          {post.category && (
            <>
              <span className="mx-1.5">/</span>
              <Link to={`/blog/category/${post.category.slug}`} className="hover:text-slate-900">{post.category.name}</Link>
            </>
          )}
        </nav>

        {post.hero_image && (
          <img src={imageUrl(post.hero_image)} alt={post.hero_alt || post.title}
               className="aspect-[16/9] w-full rounded-2xl object-cover" data-testid="post-hero" />
        )}

        <header className="mt-7">
          {post.category && (
            <span className="inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide"
                  style={{ backgroundColor: `${post.category.color}22`, color: post.category.color }}>
              {post.category.name}
            </span>
          )}
          <h1 data-testid="post-title"
              className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            {post.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
            {post.author && (
              <Link to={`/blog/author/${post.author.slug}`} className="flex items-center gap-2 hover:text-slate-900">
                {post.author.avatar_url && (
                  <img src={imageUrl(post.author.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
                )}
                By <span className="font-medium text-slate-700">{post.author.name}</span>
              </Link>
            )}
            <span>{formatDate(post.publish_date)}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {post.reading_minutes} min read</span>
          </div>
        </header>

        {post.tldr && (
          <aside className="mt-8 rounded-2xl border-l-4 border-red-700 bg-white p-5" data-testid="post-tldr">
            <p className="label-caps text-red-700">TL;DR</p>
            <p className="mt-2 text-slate-700">{post.tldr}</p>
          </aside>
        )}

        <div
          data-testid="post-body"
          className="prose prose-slate mt-8 max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:mt-10 prose-h3:mt-8 prose-a:text-red-700 prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
        />

        <FAQBlock faq={post.faq} />

        {post.keywords?.length > 0 && (
          <section className="mt-10" data-testid="post-keywords">
            <p className="label-caps">Topics</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {post.keywords.map((k) => (
                <li key={k} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                  #{k}
                </li>
              ))}
            </ul>
          </section>
        )}

        {post.backlinks?.length > 0 && (
          <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-6" data-testid="post-backlinks">
            <h2 className="label-caps">Related reading on this site</h2>
            <ul className="mt-3 space-y-2">
              {post.backlinks.map((url, i) => (
                <li key={i}><Link to={url} className="font-medium text-red-700 hover:underline">{url}</Link></li>
              ))}
            </ul>
          </section>
        )}

        {post.author && (
          <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-6" data-testid="author-card">
            <div className="flex items-start gap-4">
              {post.author.avatar_url && (
                <img src={imageUrl(post.author.avatar_url)} alt="" className="h-14 w-14 rounded-full object-cover" />
              )}
              <div>
                <Link to={`/blog/author/${post.author.slug}`}
                      className="font-display text-lg font-semibold text-slate-900 hover:text-red-700">
                  {post.author.name}
                </Link>
                {post.author.bio && <p className="mt-1 text-sm text-slate-600">{post.author.bio}</p>}
              </div>
            </div>
          </section>
        )}

        <section className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6">
          <p className="text-sm font-medium text-slate-700">Share this article</p>
          <ShareBar post={post} />
        </section>

        <aside className="mt-12 rounded-2xl bg-slate-900 p-6 text-white" data-testid="post-sms-cta">
          <h2 className="font-display text-2xl font-bold">Get food recall alerts by SMS</h2>
          <p className="mt-2 text-white/70">
            One text per Australian recall that matches your state and allergens. From A$4.99 a month.
          </p>
          <Link to="/join" className="mt-5 inline-flex items-center gap-2 rounded-full bg-red-700 px-5 py-3 font-semibold text-white hover:bg-red-800">
            Turn on SMS alerts <ArrowRight className="h-4 w-4" />
          </Link>
        </aside>

        {related.length > 0 && (
          <section className="mt-12" data-testid="related-posts">
            <h2 className="font-display text-xl font-bold text-slate-900">Keep reading</h2>
            <ul className="mt-5 grid gap-5 sm:grid-cols-3">
              {related.map((r) => (
                <li key={r.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  {r.hero_image && (
                    <Link to={`/blog/${r.slug}`}>
                      <img src={imageUrl(r.hero_image)} alt="" className="aspect-[16/9] w-full object-cover" />
                    </Link>
                  )}
                  <div className="p-4">
                    <Link to={`/blog/${r.slug}`} className="font-display text-base font-semibold text-slate-900 hover:text-red-700">
                      {r.title}
                    </Link>
                    <p className="mt-1 text-xs text-slate-500">{formatDate(r.publish_date)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <SourceNote className="mt-12" />
      </article>
    </div>
  );
}
